import type { ReactNode } from "react";
import { Icon } from "@iconify/react";
import { Dropdown } from "antd";
import { Button } from "@/shared/components/button";
import { cn } from "@/shared/lib/utils";
import { NotificationsMenu } from "@/shared/layout/NotificationsMenu";
import { SidebarToggle } from "@/shared/layout/PortalSidebar";
import { GlobalSearch, type UseSearchSources } from "@/shared/components/GlobalSearch";

/** Header search: hidden on small screens, narrower on tablets, full width on desktop. */
export const HEADER_SEARCH_CLASS =
  "!hidden !h-8 !w-44 !rounded-full !border-black !bg-white !text-sm !text-[#4B5563] placeholder:!text-[#4B5563] md:!inline-flex xl:!w-64";

export interface HeaderMenuItem {
  label: string;
  onClick: () => void;
  danger?: boolean;
}

interface PortalHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  avatarSrc?: string | null;
  avatarInitials: string;
  menuItems: HeaderMenuItem[];
  searchPlaceholder?: string;
  /** What the header search finds in this portal (pages + its data). */
  useSearchSources: UseSearchSources;
  showNotifications?: boolean;
  /** Which portal's notification feed the bell shows (default: patient/caregiver). */
  notificationsVariant?: "patient" | "clinician" | "admin";
}

export function PortalHeader({
  title,
  description,
  avatarSrc,
  avatarInitials,
  menuItems,
  searchPlaceholder = "Search anything...",
  useSearchSources,
  showNotifications = false,
  notificationsVariant = "patient",
}: PortalHeaderProps) {

  const menuContent = (
    <div className="w-[150px] overflow-hidden rounded-lg border border-[#DADDE5] bg-white shadow-[0_4px_12px_rgba(17,24,39,0.14)]">
      {menuItems.map((item, index) => (
        <div key={item.label}>
          {index > 0 && <div className="h-px bg-[#E5E7EB]" />}
          <Button
            variant="ghost"
            onClick={item.onClick}
            className={cn(
              "!block !h-auto !w-full !justify-start !rounded-none !px-4 !py-3 !text-left !text-sm !font-medium",
              item.danger ? "!text-[#E4002B] hover:!bg-[#FFF2F4] hover:!text-[#E4002B]" : "!text-[#180047] hover:!bg-[#F7F7F7] hover:!text-[#180047]",
            )}
          >
            {item.label}
          </Button>
        </div>
      ))}
    </div>
  );

  return (
    <header className="flex h-[73px] w-full shrink-0 items-center justify-between gap-3 border-b border-[#E5E7EB] bg-[#F7F7F7] px-4 lg:px-6">
      {/* Left — menu (below lg) + page title + description */}
      <div className="flex min-w-0 items-center gap-2">
        <SidebarToggle />
        <div className="flex min-w-0 flex-col justify-center gap-0">
          <h2 className="truncate text-base tracking-tight text-[#111827] sm:text-lg" style={{ fontWeight: 900, letterSpacing: "-0.02em" }}>
            {title}
          </h2>
          {description && <p className="hidden truncate text-[11px] leading-tight text-[#9CA3AF] sm:block">{description}</p>}
        </div>
      </div>

      {/* Right — Search + Bell + Avatar */}
      <div className="flex shrink-0 items-center gap-2 lg:gap-3">
        <GlobalSearch useSources={useSearchSources} placeholder={searchPlaceholder} className={HEADER_SEARCH_CLASS} />

      
        {showNotifications && <NotificationsMenu variant={notificationsVariant} />}

        <Dropdown
          trigger={["click"]}
          popupRender={() => menuContent}
          placement="bottomRight"
        >
          <Button
            variant="ghost"
            aria-label="Open profile menu"
            className="!flex !h-9 !w-9 !items-center !justify-center !rounded-full !bg-[#180047] !p-0 !text-center !text-xs !font-bold !leading-none !text-white hover:!bg-[#180047] hover:!opacity-85"
          >
            {avatarSrc ? (
              <img src={avatarSrc} alt="" className="h-full w-full rounded-full object-cover" />
            ) : (
              <span className="block leading-none !text-white">{avatarInitials}</span>
            )}
          </Button>
        </Dropdown>
      </div>
    </header>
  );
}
